<template>
	<div id="modal-train-model" class="fullscreen">
		
		<div class="rp-modal flex flex-col items-center justify-center" style="width: 950px; height: fit-content;">

			<template v-if="layout.currentModel.status === 'training' || layout.currentModel.status === 'finished'">
				<div class="my-4 text-lg font-medium">{{ layout.currentModel.status === 'training' ? $t('Model is training, please wait ...') : $t('Model has finished successfully!') }}</div>

				<div class="flex flex-row justify-between items-center gap-5" style="background-color: white;">
					
					<div style="height: 400px; width: 500px;">
						<Line :data="chartData" :options="chartOptions" />
					</div>

				</div>
			</template>

			<template v-else-if="layout.currentModel.status === 'pending'">

				<div class="col-span-12">
					<div class="intro-y flex flex-col justify-center items-center  text-center mt-20 mb-5 mx-10">
						<LoadingIcon icon="oval" class="w-20 h-20 mb-5" /><br />
						{{$t(`We're getting your training environment ready`)}}.<br /> 
						{{$t('This can take a few minutes')}}.<br /> 
					</div>
				</div>
				
			</template>
			
			<div v-if="layout.currentModel.status" class="flex flex-row items-center gap-5 pt-10 pb-5" style="background-color: white;">

				<button v-if="layout.currentModel.status === 'training'"
					type="button"
					style="padding-left: 20px; padding-right: 20px;"
					class="rp-btn-action-danger">
					{{ $t('Stop training') }}
				</button>

				<button v-if="layout.currentModel.status === 'training' || layout.currentModel.status === 'pending'"
					type="button"
					class="rp-btn-action-secondary"
					style="padding-left: 20px; padding-right: 20px;"
					@click="$emit('closeModal')">
					{{ $t('Continue') }}
				</button>

				<button v-if="layout.currentModel.status === 'finished'"
					type="button"
					class="rp-btn-action-primary"
					style="padding-left: 20px; padding-right: 20px;"
					@click="$emit('trainFinished', layout.currentModel.id)">
					{{ $t('Continue') }}
				</button>

			</div>

		</div>

	</div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { Line } from 'vue-chartjs'

import {
	Chart as ChartJS,
	LineElement,
	CategoryScale,
	LinearScale,
	PointElement,
	Legend,
	Tooltip,
	Title
} from 'chart.js';

import * as rosepetalModel	from "rosepetal-model";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Legend, Tooltip, Title)

let $firebase = rosepetalModel.default._firebase;

const props = defineProps({
	model: {
		type: Object,
		required: true
	}
})

const layout = ref({
	currentModel: {}
})

const chartData = ref({
	labels: ['0'],
	datasets: [
		{
			label: 'box_mAP50',
			data: [0],
			fill: false,
			tension: 0.5
		}
	]
})

const chartOptions = {
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: true,
			position: 'top',
			labels: {
				boxHeight: 12,
				padding: 20
			}
		}
	},
	scales: {
		x: {
			title: {
				display: true,
				text: 'Epochs',
				padding: {
					top: 20
				},
				font: {
					size: 14
				}
			}
		},
		y: {
			min: 0,
			max: 1,
		}
	}
}

const loadTrainigChart = (log = []) => {
	if (!Array.isArray(log) || log.length === 0) return

	const preferredMetric = (() => {
		if ('mask_mAP50' in log[0]) return 'mask_mAP50'
		if ('box_mAP50' in log[0]) return 'box_mAP50'
		if ('top1_acc' in log[0]) return 'top1_acc'
		return null
	})()

	if (!preferredMetric) return

	const data = log
		.filter(entry => entry[preferredMetric] !== undefined && entry[preferredMetric] !== null)
		.map(entry => parseFloat(entry[preferredMetric]))

	const labels = log.map((entry, index) => `${entry.epoch || index + 1}`)

	chartData.value = {
		labels,
		datasets: [
			{
				label: preferredMetric,
				data,
				fill: false,
				borderColor: '#17356e',
				tension: 0.1
			}
		]
	}
}

let unsubscribeStatus = null;
let unsubscribeTrainingLog = null;

onMounted(() => {
	if (props.model) {
		layout.value.currentModel = props.model;
	}

	loadTrainigChart(props.model.trainingLog || []);

	unsubscribeStatus = $firebase.trackDocumentFieldChanges('model',props.model.id, (newStatus, _) => {
		layout.value.currentModel.status = newStatus;
	}, 'status');

	unsubscribeTrainingLog = $firebase.trackDocumentFieldChanges('model',props.model.id, (updatedLog, _) => {
		loadTrainigChart(updatedLog);
	}, 'trainingLog');
});

onUnmounted(() => {
	if (unsubscribeStatus) {
		unsubscribeStatus()
		unsubscribeStatus = null
	}
	if (unsubscribeTrainingLog) {
		unsubscribeTrainingLog()
		unsubscribeTrainingLog = null
	}
})
</script>

<style>
.fullscreen { 
    position: fixed; 
    left: 0; 
    top: 0; 
    width: 100%; 
    height: 100%; 
    background-color: #f2f3f3; 
    z-index: 99999; 
    overflow: hidden;
    justify-items: center;
}
</style>